import arrowDown from '@/images/ArrowDown.svg';
import theme from '@/theme';
import styled from '@emotion/styled';
import { Paper, SxProps } from '@mui/material';
import Autocomplete, {
    AutocompleteChangeReason,
    AutocompleteInputChangeReason,
    AutocompleteRenderGroupParams,
    AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import React, { HTMLAttributes, JSXElementConstructor } from 'react';
import { MaskedIcon } from '../MaskedIcon/MaskedIcon';

const IsNewAdornment = styled.div<{ show: boolean }>`
    display: ${({ show }) => (show ? 'flex' : 'none')};
    align-items: center;
    background-color: ${({ theme }) => theme.colors.primary.bg};
    color: ${({ theme }) => theme.colors.primary.main};
    font-size: 12px;
    font-weight: bold;
    padding: 2px 4px;
    border-radius: 4px;
    height: 1.5rem;

    ::after {
        content: 'PRO+';
    }
`;

const OptionContent = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-grow: 1;
    margin-left: 1rem;

    &:last-child {
        margin-left: auto;
    }
`;

const OptionLabelWrapper = styled.div`
    height: 3rem;
    display: flex;
    align-items: center;
    gap: 5px;
    line-height: 2rem;
    overflow: hidden;
`;

const OptionLabel = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 400px;
`;

function renderOption<T>(
    props: React.HTMLAttributes<HTMLLIElement>,
    option: T,
    getOptionLabel: (option: T) => string,
    adornmentImage: JSX.Element | null,
    isProPlus: boolean = false,
    endAdornment?: React.ReactNode,
    value?: T,
    optionSx?: any
) {
    return (
        <Box
            component="li"
            sx={{
                '& > img': { mr: 2, flexShrink: 0 },
                backgroundColor: `${option === value ? `${theme.colors.black[100]} !important` : 'none'}`,
                '&:hover': {
                    backgroundColor: `${theme.colors.black[100]} !important`,
                },
                ...optionSx,
            }}
            {...props}
            key={props.id}>
            <OptionContent>
                <OptionLabelWrapper>
                    {adornmentImage}
                    <OptionLabel>{getOptionLabel(option)}</OptionLabel>
                    <IsNewAdornment show={isProPlus} />
                </OptionLabelWrapper>
                {endAdornment}
            </OptionContent>
        </Box>
    );
}

const renderInput = (params: AutocompleteRenderInputParams, label: string, adornmentImage: JSX.Element | null, placeholder?: string) => {
    return (
        <TextField
            {...params}
            id="country-select-textfield"
            label={label}
            placeholder={placeholder ?? 'Search...'}
            InputProps={{
                ...params.InputProps,
                startAdornment: adornmentImage ? (
                    <InputAdornment position="start" sx={{ marginRight: 0 }}>
                        {adornmentImage}
                    </InputAdornment>
                ) : null,
            }}
            inputProps={{
                ...params.inputProps,
                'data-hj-allow': '',
            }}
            variant="outlined"
        />
    );
};

const StyledPaper = styled(Paper, {
    shouldForwardProp: prop => prop !== 'hasNoOptionsText',
})<{ hasNoOptionsText: boolean }>`
    .MuiAutocomplete-noOptions {
        ${({ hasNoOptionsText }) => (hasNoOptionsText ? `padding: 0;` : '')}
    }
`;
const CustomPaper = ({ sx, ...props }: any) => {
    return <StyledPaper elevation={8} sx={{ marginBottom: '1rem', ...sx }} {...props} />;
};

const CAutoComplete = styled(Autocomplete)`
    & .MuiInputBase-root {
        border-radius: 8px;
        padding-left: 2rem;
        background-color: #fff;
    }

    & label.Mui-focused {
        color: ${({ theme }) => theme.colors.secondary.main} !important;
    }

    & .Mui-disabled .MuiOutlinedInput-notchedOutline {
        border-color: #e6e6e6 !important;
    }
    & .MuiAutocomplete-inputRoot {
        &.Mui-focused .MuiOutlinedInput-notchedOutline {
            border: 1px solid ${({ theme }) => theme.colors.secondary.main};
        }
    }

    & .MuiAutocomplete-endAdornment {
        /* TODO: check other dropdowns */
        top: 50% !important;
    }
` as typeof Autocomplete;

const Group = styled.div`
    font-size: 1.25rem;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    padding: 0.3rem 0 0.6rem 1.25rem;
`;

type FreeSoloMapping<FreeSolo extends boolean> = FreeSolo extends true ? string : never;

interface Props<T, FreeSolo extends boolean> {
    /**
     * The id of the select
     */
    id: string;
    /**
     * The label of the select
     */
    label?: string;
    /**
     * The options of the select
     */
    options: T[];
    /**
     * The function to get the label of the option
     */
    getOptionLabel: (option: T | FreeSoloMapping<FreeSolo>) => string;
    /**
     * The function to get the start adornment of the option
     */
    getStartAdornment?: (option: T | string) => JSX.Element | null;
    /**
     * The class name of the select to override styles
     */
    className?: string;
    /**
     * The function to to get the end adornment of the option
     */
    getEndAdornment?: (option: T) => React.ReactNode;
    /**
     * On change event
     */
    onChange?: (event: React.SyntheticEvent, value: T | null | FreeSoloMapping<FreeSolo>, reason: AutocompleteChangeReason) => void;
    /**
     * On input change event - only useable when freeSolo is true
     */
    onInputChange?: FreeSolo extends true
        ? (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => void
        : never;
    /**
     * If the select is disabled
     */
    disabled?: boolean;
    /**
     * group by function
     */
    groupBy?: (option: T) => string;
    /**
     * The value of the select
     */
    value?: T;
    /**
     * The function to get if the option is new
     */
    isProPlus?: (option: T) => boolean;
    /**
     * The component to use for the listbox
     */
    listboxComponent?: JSXElementConstructor<HTMLAttributes<HTMLElement>>;
    /**
     * The component to show when there are no options
     */
    noOptionsText?: React.ReactNode;
    /**
     * The style of the option
     */
    optionSx?: SxProps;
    /**
     * The style of the paper
     */
    paperSx?: SxProps;
    /**
     * If set to true, the clear button will be hidden
     */
    disableClearable?: boolean;
    /**
     * Placeholder text
     */
    placeholder?: string;
    /**
     * If set to true, the user will be able to input free text
     */
    freeSolo?: FreeSolo;
    /**
     * Force the visibility display of the popup icon.
     * @default 'auto'
     */
    forcePopupIcon?: true | false | 'auto';
}

/**
 * A select with adornment
 */
export function AdornmentSelect<T, FreeSolo extends boolean = false>({
    id,
    value,
    label = '',
    options,
    onChange,
    onInputChange,
    getOptionLabel,
    getStartAdornment = _ => null,
    groupBy,
    disabled,
    isProPlus,
    className,
    getEndAdornment,
    listboxComponent,
    noOptionsText,
    optionSx,
    paperSx,
    disableClearable,
    placeholder,
    freeSolo,
    forcePopupIcon,
}: Props<T, FreeSolo>) {
    return (
        <CAutoComplete
            id={id}
            className={className}
            options={options}
            groupBy={groupBy}
            autoHighlight
            getOptionLabel={getOptionLabel}
            value={value}
            onChange={onChange}
            onInputChange={onInputChange}
            PaperComponent={props => <CustomPaper {...props} sx={paperSx} hasNoOptionsText={!!noOptionsText} />}
            ListboxComponent={listboxComponent}
            noOptionsText={noOptionsText}
            popupIcon={<MaskedIcon src={arrowDown} size="24px" />}
            disabled={disabled}
            disableClearable={disableClearable}
            freeSolo={freeSolo}
            forcePopupIcon={forcePopupIcon}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: T) =>
                renderOption<T>(
                    props,
                    option,
                    getOptionLabel,
                    getStartAdornment(option),
                    isProPlus?.(option),
                    getEndAdornment?.(option),
                    value,
                    optionSx
                )
            }
            renderGroup={(params: AutocompleteRenderGroupParams) => (
                <div key={params.key}>
                    <Group>{params.group}</Group>
                    {params.children}
                </div>
            )}
            renderInput={(params: AutocompleteRenderInputParams) =>
                renderInput(params, label, getStartAdornment(`${params.inputProps.value}`), placeholder)
            }
        />
    );
}

export default AdornmentSelect;
